import { Button, Flex, Form, Input, InputNumber } from "antd";
import { useEffect } from "react";
import { useParams } from "react-router";
import {
  useCreateQuestionMutation,
  useUpdateQuestionMutation,
} from "../../../../app/features/adminContent/adminContent.api";
import CustomModal from "../../../../components/CustomModal";
import useError from "../../../../hooks/useError";
import type { ModalType } from "../../../../hooks/useModal";
import { toast } from "../../../../lib/toast";

interface Props {
  open: boolean;
  onCancel: () => void;
  initialValues?: any | null;
  type?: ModalType["type"];
}

export default function ManageQuizModal({
  open,
  onCancel,
  initialValues,
  type,
}: Props) {
  const { quizId } = useParams();
  const [form] = Form.useForm();

  // Mutation API
  const [
    createQuestion,
    { isLoading: createLoading, isSuccess: createSuccess, error: createError },
  ] = useCreateQuestionMutation();
  const [
    updateQuestion,
    { isLoading: updateLoading, isSuccess: updateSuccess, error: updateError },
  ] = useUpdateQuestionMutation();
  // Error Handeling
  useError(createError, "Quiz Question Create Failed");
  useError(updateError, "Quiz Question Update Failed");

  const handleSubmit = async (values: any) => {
    const newData = {
      ...values,
      type: "SINGLE_SELECT",
    };

    if (type !== "UPDATE") {
      newData.quizId = Number(quizId);
    }

    if (initialValues && type === "UPDATE") {
      // 🔥 call mutation with both id and body
      await updateQuestion({
        id: initialValues?.id, // <--- pass the quiz id here
        body: newData, // <--- pass the updated data
      });
    } else {
      await createQuestion(newData);
    }
  };

  useEffect(() => {
    if (initialValues && type === "UPDATE") {
      form.setFieldsValue({
        prompt: initialValues?.prompt,
        marks: initialValues?.marks,
        sortOrder: initialValues?.sortOrder,
      });
    }
  }, [initialValues, form, open, type]);

  useEffect(() => {
    if (createSuccess) {
      toast("success", "Quiz Question Created Successfully");
      onCancel();
    }
  }, [createSuccess]);

  useEffect(() => {
    if (updateSuccess) {
      toast("success", "Quiz Question Updated Successfully");
      onCancel();
    }
  }, [updateSuccess]);

  return (
    <CustomModal
      handleClose={onCancel}
      title={type === "UPDATE" ? "Update Quiz" : "Create Quiz"}
      open={open}
      width={500}
      footer
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="custom-form"
        size="large"
      >
        <Form.Item
          label="Question Title"
          name="prompt"
          rules={[{ required: true, message: "Please enter questions title" }]}
        >
          <Input placeholder="Enter Question Title" />
        </Form.Item>

        <Form.Item
          label="Marks"
          name="marks"
          rules={[{ required: true, message: "Please enter marks" }]}
        >
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            placeholder="Enter Marks"
          />
        </Form.Item>

        <Form.Item
          label="Sort Order"
          name="sortOrder"
          rules={[{ required: true, message: "Please enter sort order" }]}
        >
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            placeholder="Enter sort order"
          />
        </Form.Item>

        <Flex justify="end" gap={8} className="mt-12">
          <Button
            className="min-w-[120px]"
            disabled={createLoading || updateLoading}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            className="min-w-[120px]"
            htmlType="submit"
            loading={createLoading || updateLoading}
          >
            {type === "UPDATE" ? "Update" : "Create"}
          </Button>
        </Flex>
      </Form>
    </CustomModal>
  );
}
